import React, { useState, useRef } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { Users, Search, RefreshCw, CheckCircle, XCircle } from "lucide-react";
import { ToastContainer } from "react-toastify";
import useMyndas from "hooks/useMyndas";
import { Mynda } from "services/mynda.service";

const LIMIT = 20;

const Myndas = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const [employeeType, setEmployeeType] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const { data: response, isLoading, isError, refetch } = useMyndas({
    page,
    limit: LIMIT,
    search: search || undefined,
    employeeType: employeeType || undefined,
  });

  const myndas: Mynda[] = response?.data?.data ?? [];
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const handleSearch = (value: string) => {
    setDraftSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 400);
  };

  const handleTypeFilter = (value: string) => {
    setEmployeeType(value);
    setPage(1);
  };

  return (
    <React.Fragment>
      <BreadCrumb title="Workers" pageTitle="Mynda" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter this page by name, state or LGA..."
              value={draftSearch}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9 w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            />
          </div>
          <select
            value={employeeType}
            onChange={(e) => handleTypeFilter(e.target.value)}
            className="border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Workers</option>
            <option value="Mynda">Independent</option>
            <option value="Agency">Agency-based</option>
          </select>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{total} total</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Worker</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Occupation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Verified</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Views / Bookings</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={6} label="Couldn't load myndas" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : myndas.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No workers found</p>
                  </td>
                </tr>
              ) : (
                myndas.map((m) => {
                  const initials = `${m.user?.firstName?.[0] ?? ""}${m.user?.lastName?.[0] ?? ""}`.toUpperCase();
                  return (
                    <tr key={m._id} className="hover:bg-gray-50 transition-colors">
                      <td data-label="Worker" data-cell="title" className="px-4 py-3">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          {m.user?.profilePicture?.url ? (
                            <img
                              src={m.user.profilePicture.url}
                              alt={initials}
                              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 flex-shrink-0">
                              {initials || "?"}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {m.user?.firstName} {m.user?.lastName}
                            </p>
                            <p className="text-xs text-gray-400 truncate">{m.user?.phoneNumber}</p>
                          </div>
                        </div>
                      </td>
                      <td data-label="Occupation" className="px-4 py-3">
                        <p className="text-gray-700 truncate max-w-[160px]">
                          {m.occupation?.join(", ") || "—"}
                        </p>
                      </td>
                      <td data-label="Location" data-cell="block" className="px-4 py-3 whitespace-nowrap">
                        <p className="text-gray-700">{m.state || "—"}</p>
                        {m.lga && <p className="text-xs text-gray-400">{m.lga}</p>}
                      </td>
                      <td data-label="Type" className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          m.agency
                            ? "bg-violet-50 text-violet-700"
                            : "bg-sky-50 text-sky-700"
                        }`}>
                          {m.agency ? "Agency" : "Independent"}
                        </span>
                      </td>
                      <td data-label="Verified" className="px-4 py-3">
                        {m.accountVerified ? (
                          <CheckCircle className="size-4 text-green-500" />
                        ) : (
                          <XCircle className="size-4 text-gray-300" />
                        )}
                      </td>
                      <td data-label="Views / Bookings" className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        <span className="font-medium">{m.views ?? 0}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span className="font-medium">{m.bookings ?? 0}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} · {total} total
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Myndas;
